import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchBlockListRequest,
  deleteBlockNumberRequest,
} from "../../../redux/Blocklist/BlocklistSlice";
import { RiDeleteBin6Line } from "react-icons/ri";
import TableCompo from "../Table/TableCompo";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
} from "@mui/material";
import { toast } from "react-toastify";
import { apiRequest } from "../../../redux/Reports/reportsSlice";
import useCsvConverter from "../../../hooks/convertToCSV";
import { canShowPermission } from "../Permissions/permissionObject";

const BlockListTable = ({ refresh }) => {
  const dispatch = useDispatch();
  const {
    blocklists = [],
    totalCount = 0,
    loading,
    error,
  } = useSelector((state) => state.blocklist || {});

  const [pagination, setPagination] = useState({ start: 0, limit: 5 });
  const [searchText, setSearchText] = useState("");
  const [isProcessingYes, setIsProcessingYes] = useState(false);
  const [isProcessingNo, setIsProcessingNo] = useState(false);
  const [confirmDeleteDialogOpen, setConfirmDeleteDialogOpen] = useState(false);
  const [selectedPhone, setSelectedPhone] = useState(null);

  useEffect(() => {
    dispatch(fetchBlockListRequest({ ...pagination, search: searchText }));
  }, [dispatch, refresh, pagination, searchText]);

  const handleTableChange = (action, tableState) => {
    if (action === "changePage") {
      setPagination((prev) => ({
        ...prev,
        start: tableState.page * prev.limit,
      }));
    } else if (action === "changeRowsPerPage") {
      setPagination({
        start: 0,
        limit: tableState.rowsPerPage,
      });
    } else if (action === "search") {
      setSearchText(tableState.searchText || "");
      setPagination((prev) => ({
        ...prev,
        start: 0,
      }));
    }
  };

  const handleDeleteClick = (phone) => {
    setSelectedPhone(phone);
    setConfirmDeleteDialogOpen(true);
  };

  const handleConfirmDeleteYes = () => {
    if (!selectedPhone) return;

    setIsProcessingYes(true); // start loader for Yes

    setTimeout(() => {
      dispatch(deleteBlockNumberRequest(selectedPhone));
      setConfirmDeleteDialogOpen(false);

      setSelectedPhone(null);
      setIsProcessingYes(false); // stop loader
      toast.success("Deleted block number successfully");
    }, 1000);
  };

  const handleConfirmDeleteNo = () => {
    setIsProcessingNo(true); // start loader for No

    setTimeout(() => {
      setConfirmDeleteDialogOpen(false);
      setSelectedPhone(null);
      setIsProcessingNo(false); // stop loader
      toast.error("Delete block number cancelled");
    }, 500); // shorter delay for cancel
  };

  const [permissions, setPermissions] = useState({});

  useEffect(() => {
    const storedPermissions = localStorage.getItem("userPermissions");
    if (storedPermissions) {
      try {
        setPermissions(JSON.parse(storedPermissions));
      } catch (e) {
        console.error("Invalid permissions JSON", e);
      }
    }
  }, []);
  const canEdit = canShowPermission(permissions, "blockCustomer", "edit");
  const canDelete = canShowPermission(permissions, "blockCustomer", "edit");

  const columns = [
    { field: "phone", headerName: "Phone" },
    {
      field: "createdAt",
      headerName: "Added On",
      render: (val, row) => {
        const createdAt = row.createdAt;
        return createdAt
          ? new Date(createdAt).toLocaleString("en-IN", {
              day: "2-digit",
              month: "short",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
              hour12: true,
            })
          : "N.A";
      },
    },
    ...(canDelete
      ? [
          {
            field: "delete",
            headerName: "Action",
            render: (value, row) => (
              <Button
                onClick={() => handleDeleteClick(row.phone)}
                variant="contained"
                sx={{
                  backgroundColor: "green",
                  color: "white",
                  textTransform: "none",
                  cursor: "pointer",
                  "&:hover": {
                    backgroundColor: "#006400",
                  },
                }}
              >
                Unblock
              </Button>
            ),
          },
        ]
      : []),
    {
      field: "createdBY",
      headerName: "Created By",
      width: 200,
      hideInExcel: true,
      render: (value, row) => {
        return row?.createdBy ? row.createdBy.name : "N/A";
      },
    },
  ];

  const { convertToCsv } = useCsvConverter();

  const triggerCsvDownload = (csvText, filename) => {
    const blob = new Blob([csvText], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();

    window.URL.revokeObjectURL(url);
  };

  const handleDownloadStoreCsv = () => {
    console.log("Store CSV API called");

    dispatch(
      apiRequest({
        method: "GET",
        endpoint: "/api/v1/blocklist/blocklistCsv",
        query: {},
        callback: (response) => {
          const csv = convertToCsv(response?.data || []);
          triggerCsvDownload(csv, "blocklist.csv");
        },
      })
    );
  };

  return (
    <div>
      {loading ? (
        <p>Loading...</p>
      ) : (
        <TableCompo
          columns={columns}
          data={JSON.parse(JSON.stringify(blocklists)).reverse()}
          onTableChange={handleTableChange}
          totalCount={totalCount}
          page={pagination.start / pagination.limit}
          limit={pagination.limit}
          loading={loading}
          extraButtons={
            <Button
              size="sm"
              variant="outlined"
              onClick={handleDownloadStoreCsv}
            >
              Download CSV
            </Button>
          }
        />
      )}

      {/*  Delete Confirmation Dialog */}
      <Dialog open={confirmDeleteDialogOpen} onClose={handleConfirmDeleteNo}>
        <DialogTitle>Delete Blocked Number</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the number{" "}
            <strong>{selectedPhone}</strong> from the blocklist?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDeleteYes}
            disabled={isProcessingYes}
          >
            {isProcessingYes ? "Processing..." : "Yes"}
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmDeleteNo}
            disabled={isProcessingNo}
          >
            {isProcessingNo ? "Processing..." : "No"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default BlockListTable;
